import * as React from 'react';
import { Translate } from 'react-redux-i18n';
import { Dispatch } from 'react-redux';

import { StoreState } from '../../../../reducers';
import FieldPanel from './FieldPanel';
import { PdfSettingsPanelProps } from './PdfSettingsPanel';
import PdfSettingsPanel from '../../../../containers/Editor/EditPanel/SettingsPanel/PdfSettingsPanel';
import CopyPublicationUrl from '../../../../containers/Editor/EditPanel/SettingsPanel/CopyPublicationUrl';
import { StyledList } from './styles';
import { Button } from '../styles';
import {
	getPublicationSettings,
	savePublicationSettings,
	setSettingsError,
} from '../../../../containers/Editor/actions';
import ValidationUtils from '../../../../utils/validation';
import { Api } from '../../../../typings/api';
import Utils from '../../../../utils';

interface SettingsPanelProps {
	publicationId: number;
	settingNames: Api.PublicationSettingName[];
	settings: Api.PublicationSettings;
	serverErrors: { [key: string]: string | null };
	dispatch: Dispatch<StoreState>;
}

interface CustomPanelProps {
	onClose: () => void;
}

type SettingData = {
	label: string;
	data?: {
		name: Api.PublicationSettingName;
		label: string;
		icon: string;
		placeholder?: string;
		hint?: string;
		checkIsValid?: (value: string) => boolean;
		getMaskedValue?: (value: string) => string;
		renderChildren?: (value: string) => void;
	};
	getCustomPanel?: (props: CustomPanelProps) => React.ReactNode;
};

type SettingsConfig = Record<Api.PublicationSettingName, SettingData>;

const SETTINGS_CONFIG: SettingsConfig = {
	[Api.PublicationSettingName.downloadPdf]: {
		label: 'messages.add_pdf_to_download',
		getCustomPanel: (props) => <PdfSettingsPanel {...props as PdfSettingsPanelProps} />
	},
	[Api.PublicationSettingName.customUrl]: {
		label: 'messages.customize_url',
		data: {
			name: Api.PublicationSettingName.customUrl,
			label: 'messages.your_url',
			icon: 'iconset_uprez_link2',
			placeholder: 'messages.campaign_name',
			hint: 'messages.customize_url_hint',
			getMaskedValue: (value) => value.replace(/\s/g, '_'),
			renderChildren: (value) => <CopyPublicationUrl customUrl={value} />,
		},
	},
	[Api.PublicationSettingName.googleAnalyticsUa]: {
		label: 'messages.integrate_ua_code',
		data: {
			name: Api.PublicationSettingName.googleAnalyticsUa,
			label: 'messages.your_ua_code',
			icon: 'iconset_uprez_tracking_stats',
			placeholder: 'UA-000000-2',
			hint: 'messages.integrate_ua_code_hint',
			checkIsValid: (value) => {
				const uaCodes = value.replace(/\s/g, '').split(/,|;/);
				return uaCodes.every(code => ValidationUtils.isGoogleAnalyticsUaCode(code));
			},
		},
	},
};

const SettingsPanel = ({
	publicationId,
	settingNames,
	settings,
	serverErrors,
	dispatch,
}: SettingsPanelProps) => {
	const [activeSetting, setActiveSetting] = React.useState<Api.PublicationSettingName | null>(null);
	const prevEditedSetting = React.useRef<Api.PublicationSettingName | null>(null);

	const handleSettingSave = (name: Api.PublicationSettingName, value: string) => {
		prevEditedSetting.current = name;
		setActiveSetting(null);
		dispatch(savePublicationSettings(publicationId, {
			[name]: value,
		} as Api.PublicationSettings));
	};

	const resetServerError = (name: Api.PublicationSettingName) => {
		dispatch(setSettingsError(name, null));
		prevEditedSetting.current = null;
	};

	React.useEffect(() => {
		dispatch(getPublicationSettings(publicationId));
	}, [publicationId]);

	React.useEffect(() => {
		if (!Utils.isEmptyObject(serverErrors)) {
			if (prevEditedSetting.current) {
				setActiveSetting(prevEditedSetting.current);
			}
		}
	}, [serverErrors]);

	const getActiveSettingPanel = () => {
		const { getCustomPanel, data } = SETTINGS_CONFIG[activeSetting as Api.PublicationSettingName];

		if (getCustomPanel) {
			return getCustomPanel({
				onClose: () => setActiveSetting(null),
			});
		}

		if (data) {
			const { name } = data;
			const serverError = serverErrors[name];

			return (
				<FieldPanel
					{...data}
					value={settings[name] || ''}
					serverError={serverError}
					onConfirm={handleSettingSave}
					onClose={() => setActiveSetting(null)}
					resetServerError={resetServerError}
				/>
			);
		}

		return null;
	};

	if (activeSetting) {
		return <>{getActiveSettingPanel()}</>;
	}

	return (
		<StyledList>
			{Object.entries(SETTINGS_CONFIG).map(([key, { label }], index) => {
				if (settingNames.includes(key as Api.PublicationSettingName)) {
					return (
						<li key={index}>
							<Button
								type="button"
								onClick={() => setActiveSetting(key as Api.PublicationSettingName)}
							>
								<Translate value={label} />
							</Button>
						</li>
					);
				}

				return null;
			})}
		</StyledList>
	);
};

export default SettingsPanel;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/Editor/EditPanel/SettingsPanel/index.tsx